/**
 * Event workspace data access for the admin SPA (#83).
 *
 * Talks to the Go service via the shared api-client. Mirrors the JSON shape
 * of the legacy Next.js workspace endpoints. Every endpoint below is live
 * in go-service under `/api/admin/*` (see go-service/internal/api/routes.go).
 *
 * NOTE: the send-* / notify-contractors endpoints moved under `/api/admin/`
 * (#116) so the Firebase admin middleware gates them — they are operator-only
 * and have no public token auth of their own.
 */
import { apiFetch } from "../../../shared/api-client";
import { normalizeBookingItemType } from "./booking-item-types";
import type { BookingPhase } from "./booking-phase";

// Job sheet templates (#188) replaced the two-value visibility policy enum:
// an Assignment carries an optional template override (jobsheetTemplateId) and
// the server resolves the effective template name (override → Contractor
// default → Company default) as jobsheetTemplateName.

// ─── Workspace event data shape ─────────────────────────────────────────────
// Mirrors the JSON returned by GET /api/admin/events/{id}/details. Kept loose
// — the page forwards these fields, it does not re-interpret them.

/** One customer-uploaded inquiry document (#168): display name + view URL. */
export interface InquiryDocument {
  name: string;
  url: string;
}

/**
 * One Assignment as shipped on the workspace read. Named so the workspace
 * sections can `Pick` their slice from it instead of re-declaring divergent
 * subsets (sweep 2 Pass D candidate 11).
 */
export interface WorkspaceAssignment {
  id: string;
  contractorId: string;
  contractor: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
    skills: string[];
    bank_account: string | null;
    jobsheet_template_id?: string | null;
  } | null;
  status: string;
  hourlyRate: number | null;
  estimatedHours: number | null;
  tasksDescription: string | null;
  /** The vehicle this Contractor drives; null = none (migration 120). */
  vehicleName: string | null;
  /** Assignment-level template override; null = inherit (#188). */
  jobsheetTemplateId: string | null;
  /** Effective resolved template name; null when unresolvable (legacy). */
  jobsheetTemplateName: string | null;
  payAmount: number | null;
  // Contractor-visible gear list on the job sheet — feeds the job-summary
  // staleness comparison. Ships as a string[] (incl. empty []) or null.
  equipmentAssigned?: string[] | null;
  paymentStatus: string;
  paymentConfirmedAt: string | null;
  jobsheetUrl: string | null;
  reminderSentAt: string | null;
}

export interface WorkspaceEventData {
  booking: {
    id: string;
    quote_number: string;
    event_name: string | null;
    event_date: string;
    event_end_date: string | null;
    event_time: string | null;
    location: string | null;
    client_name: string;
    client_email: string;
    billing_email: string | null;
    client_phone: string;
    organization: string | null;
    booking_type: string;
    created_at: string;
    client_approved_at: string | null;
    cancelled_at?: string | null;
    gear_shortfalls?: Array<{
      role: string;
      qty_short: number;
      /** Operator-facing Role name, resolved server-side from the Booking's
       *  Profile. Absent when the Role is no longer in the catalogue. */
      role_label?: string;
    }> | null;
    /**
     * Set when another Booking's approval took Equipment this one quotes on
     * overlapping dates (#314). Cleared by a re-pick.
     */
    gear_conflict_at?: string | null;
    quote_total: number | null;
    crew_count: number | null;
    approval_token: string;
    contractor_selection_token: string | null;
    call_time: string | null;
    pack_out_time: string | null;
    site_available_from: string | null;
    call_out_notes: string | null;
    vehicle_type: string | null;
    band_names?: string | null;
    vehicle_amount?: number | null;
    vehicle_contractor_id?: string | null;
    quote_summary_generated_at?: string | null;
    job_summary_generated_at?: string | null;
    contractors_notified_at?: string | null;
    event_type?: string | null;
    package_size?: string | null;
    indoor_outdoor?: string | null;
    has_stage?: boolean;
    stage_details?: string | null;
    power_access?: string | null;
    needs_generator?: boolean;
    wet_weather_plan?: string | null;
    venue_contact?: string | null;
    purchase_order?: string | null;
    attendance?: string | null;
    content_requirements?: string[];
    execution_notes?: string[];
    tech_rider_file_id?: string | null;
    /** Route distance snapshotted by transport compute (#23). */
    transport_route_km?: number | null;
    deposit_invoice_sent_file_id?: string | null;
    balance_invoice_sent_file_id?: string | null;
    admin_header_notes?: string | null;
    status_override_phase?: BookingPhase | null;
    status_override_base_phase?: BookingPhase | null;
    status_override_updated_at?: string | null;
    derived_phase?: BookingPhase;
    display_phase?: BookingPhase;
    display_phase_step?: number;
    inquiry_path?:
      | "quick_enquiry"
      | "full_brief"
      | "dry_hire"
      | "phone_call"
      | null;
    preferred_contact_method?: "email" | "phone" | "text" | null;
    request_summary?: string | null;
    /** Booking-level quote template (#188 follow-on); null inherits the
     * Company default, then the full-quote floor. */
    quote_template_id?: string | null;
    /** True when the sent quote's sections snapshot no longer matches the
     * Booking's resolved template — the next send renders differently. */
    quote_template_stale?: boolean;
    /** Same drift check for the sent deposit/balance invoices' section
     * snapshot; rides the existing invoices-out-of-date regenerate flow. */
    invoice_template_stale?: boolean;
    /** Gear-list optimistic-concurrency token (#242 C2): echoed on the gear
     * PUT; a mismatch 409s and the workspace refetches. */
    items_revision?: number;
  };
  assignments: WorkspaceAssignment[];
  quoteLineItems: {
    id: string;
    description: string;
    quantity: number;
    days: number;
    unit_rate: number;
    line_total: number;
    sort_order: number;
    type?: string;
    // Grouped quote fields (#21): resolved heading snapshotted at generation
    // (null on tail lines like the percent discount), flat $ discount already
    // netted off line_total, and the note rendered under the line.
    group_heading?: string | null;
    discount?: number;
    line_note?: string | null;
    // Per-section summarise snapshot: true → the group renders collapsed
    // (heading + total) on the client-facing quote.
    group_summarised?: boolean;
    // Quote package this snapshot line belongs to (#20); null when the
    // booking has no packages.
    package_key?: string | null;
    labour_category?: "setup" | "operation" | "pack_down" | "travel" | null;
  }[];
  gearItems: {
    id: string;
    item_name: string;
    quantity: number;
    days: number;
    notes: string;
    // Resolved quote heading (#21) — server-side COALESCE(stored, role/type
    // default), so the editor can render/edit it directly. Saved back via
    // putGearItems. discount is the flat $ off this line's total.
    quote_group: string;
    discount: number;
    // Per-section summarise toggle — the group's checkbox state, uniform
    // across the group's rows.
    quote_group_summarised?: boolean;
    // Quote package this row belongs to (#20); null when the booking has no
    // packages. Round-trips through putGearItems.
    package_key?: string | null;
    labour_category?: "setup" | "operation" | "pack_down" | "travel" | null;
    equipment_id?: string | null;
    hire_rate?: number | null;
    type?: string;
    sort_order: number;
    starts_on?: string | null;
    ends_on?: string | null;
    substituted_from_equipment_id?: string | null;
    substituted_from_name?: string | null;
    substitution_reason?: string | null;
    manual_override?: boolean;
    // Supplying Warehouse (#304). Server-owned and read-only here: the picker
    // stamps it when it splits a Role's quantity across sites, which is why
    // one item can occupy two rows. Null on manual rows and pre-migration-128
    // ones.
    warehouse_id?: string | null;
    warehouse_name?: string | null;
    // Hired-in gear (PR2): whether this line is not owned stock, and
    // the suppliers that can source it (name + contact). Drives the
    // HiredInBanner.
    is_hired_in?: boolean;
    suppliers?: {
      name: string;
      email?: string | null;
      phone?: string | null;
    }[];
  }[];
  allContractors: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
    skills: string[];
    bank_account: string | null;
    jobsheet_template_id?: string | null;
  }[];
  approval: {
    payment_status: string;
    deposit_percent: number | null;
    deposit_amount: number;
    balance_status: string;
    quote_notes?: string;
    quote_valid_days?: number;
    discount?: { type: string; value?: number; [k: string]: unknown } | null;
    client_approval_token?: string;
    sent_to_client_at?: string;
    client_approved_at?: string;
    paid_at?: string;
    balance_paid_at?: string;
    client_confirmed_deposit_at?: string;
    client_confirmed_balance_at?: string;
    deposit_reminder_sent_at?: string;
    balance_invoiced_at?: string;
    balance_reminder_sent_at?: string;
    quote_reminder_sent_at?: string;
    signer_name?: string | null;
  } | null;
  paymentSummary: {
    quoteTotal: number;
    depositAmount: number;
    balanceAmount: number;
    depositStatus: string;
    balanceStatus: string;
  };
  documents: {
    quotePdf: string | null;
    techRider: string | null;
    /**
     * Every document the customer uploaded through the inquiry form (#168),
     * resolved to a display name + view URL. Empty when none were attached.
     * `techRider` above remains for the single-document legacy consumers;
     * this list is the complete set the operator can open.
     */
    inquiryDocuments: InquiryDocument[];
    depositInvoice: string | null;
    balanceInvoice: string | null;
  };
  // Quote packages (#20) — up to 3 named packages, each a complete gear
  // list. Empty for the classic single-quote flow.
  quotePackages: {
    key: string;
    name: string;
    position: number;
    draft_file_id?: string | null;
    sent_file_id?: string | null;
    draft_pdf_url?: string | null;
    sent_pdf_url?: string | null;
  }[];
  daySchedules: {
    id: string;
    day_number: number;
    schedule_date: string;
    kind: DayScheduleKind;
    site_open: string;
    call_time: string;
    event_start: string;
    event_end: string;
    pack_out: string;
    ready_by: string;
    notes: string;
  }[];
  // Generation-time input SNAPSHOTS stamped on the artifacts (#175; null when
  // never generated). The read shape the client already consumes, frozen at
  // generation. The client runs this snapshot and the current read through
  // the SAME projection and compares the strings — staleness is derived
  // client-side, never shipped as a boolean. See summary-staleness.ts.
  quoteSummarySnapshot: WorkspaceStalenessSnapshot | null;
  jobSummarySnapshot: WorkspaceStalenessSnapshot | null;
  // #232 — raw inquiry payload, surfaced to the operator adjacent to the
  // generated quote so they can see exactly what the client wrote without
  // touching the database. `additionalInfo` is extracted server-side for
  // prominent rendering; `formData` carries the full inquiry payload as a
  // plain JSON object. Render in a <pre> with `JSON.stringify(..., null, 2)`
  // — React-escaped only; never `dangerouslySetInnerHTML`. Null when the
  // booking has no inquiry attached (manual / legacy bookings).
  rawClientInput: {
    additionalInfo: string;
    formData: Record<string, unknown>;
  } | null;
  // Transport (#23): the booking's planned vehicles (feeds the job-hash
  // transport node + the Transport section) and the tenant's extra-hire-day
  // percent (a quote-hash input).
  bookingVehicles?: {
    vehicle_id: string;
    vehicle_name: string;
    vehicle_type: string;
    sort_order: number;
  }[];
  multiDayExtraDayPercent?: number;
  // Server-owned Labour Hours pool (#242 sweep 2 C4), folded by
  // internal/labour from the same service rows shipped in gearItems — the SPA
  // consumes it and never re-derives the qty=hours convention. total_hours /
  // by_category are null while multiple packages are candidates pre-approval
  // (each package carries its own labour rows, so "the" pool doesn't exist)
  // or when no labour was priced. categories is the ordered labour vocabulary
  // with display labels — the server owns it; there is no TS mirror.
  quotedLabour?: {
    total_hours: number | null;
    by_category: {
      setup: number;
      operation: number;
      pack_down: number;
      travel: number;
    } | null;
    categories: LabourCategoryOption[];
  };
}

/**
 * One labour category + its display label, as shipped on the workspace read's
 * quotedLabour.categories. internal/labour owns the vocabulary and the
 * labels; the gear editor seeds its labour rows from this list.
 */
export interface LabourCategoryOption {
  category: "setup" | "operation" | "pack_down" | "travel";
  label: string;
}

// The generation-time staleness snapshot (#175): the subset of the read payload
// the staleness projections consume. Stored server-side as JSON in the read
// shape, so the client can feed it straight into projectQuote/JobSummary
// exactly as it does the live WorkspaceEventData (summary-staleness.ts).
export type WorkspaceStalenessSnapshot = Pick<
  WorkspaceEventData,
  | "booking"
  | "gearItems"
  | "quotePackages"
  | "approval"
  | "daySchedules"
  | "assignments"
  | "bookingVehicles"
  | "multiDayExtraDayPercent"
>;

// ─── Reads ──────────────────────────────────────────────────────────────────

/** GET /api/admin/events/{id}/details — EXISTS in Go (#67). */
export async function getEventDetails(
  id: string,
  signal?: AbortSignal,
): Promise<WorkspaceEventData> {
  return apiFetch<WorkspaceEventData>(`/api/admin/events/${id}/details`, {
    signal,
  });
}

/** Equipment autocomplete search. GET /api/admin/equipment?q= — EXISTS in Go (#66). */
export interface EquipmentSearchResult {
  id: string;
  name: string;
  type: string;
  hire_rate_per_day: number;
  // Hired-in metadata (PR2): whether this item is owned stock, and the suppliers
  // that can source it (name + contact). The gear editor stamps these onto a
  // freshly-picked row so the HiredInBanner can surface it before saving.
  is_hired_in?: boolean;
  suppliers?: { name: string; email?: string | null; phone?: string | null }[];
}

export async function searchEquipment(
  query: string,
  signal?: AbortSignal,
): Promise<EquipmentSearchResult[]> {
  const data = await apiFetch<unknown>("/api/admin/equipment", {
    query: { q: query },
    signal,
  });
  return Array.isArray(data) ? (data as EquipmentSearchResult[]) : [];
}

// ─── Mutations ──────────────────────────────────────────────────────────────
// All endpoints below are live in go-service.

// Per-section workspace patch payloads. Each type mirrors the field whitelist
// of the matching section handler in go-service/internal/api/event_workspace.go
// — a key the server doesn't whitelist is silently ignored over the wire, so
// the compiler is the only thing standing between a misspelled field and a
// save that silently does nothing.

/**
 * Editable booking header columns (sections `event_info` / `client_info`).
 * Mirrors the server-side whitelist; gear-affecting fields are intentionally
 * not editable. Applied per present key — omitted fields are left alone.
 */
export const HEADER_FIELDS = [
  "event_name",
  "organization",
  "location",
  "event_time",
  "client_name",
  "client_email",
  "billing_email",
  "client_phone",
  "event_type",
  "venue_contact",
  "wet_weather_plan",
  "purchase_order",
  "event_date",
  "event_end_date",
] as const;
export type HeaderField = (typeof HEADER_FIELDS)[number];

export type EventInfoPatch = Partial<Record<HeaderField, string | null>>;

/**
 * Section `quote_settings` is a full upsert, NOT a per-key patch: the server
 * writes all four columns on every call, substituting defaults (deposit 50%,
 * 28 valid days, no discount, no notes) for anything omitted. Always send the
 * complete set — hence no optional fields. quoteTemplateId is the one
 * exception server-side (applied only when the key is present), but it rides
 * every payload here so a save can never silently reset it.
 */
export interface QuoteSettingsPatch {
  notes: string | null;
  depositPercent: number;
  quoteValidDays: number;
  gearDiscountPercent: number;
  /** Booking-level quote template; null inherits the Company default. */
  quoteTemplateId: string | null;
}

/** One per-day schedule row inside a `job_details` patch (multi-day events). */
export type DayScheduleKind = "setup" | "event" | "packout";

export interface DaySchedulePayload {
  day_number: number;
  schedule_date: string;
  kind: DayScheduleKind;
  site_open: string;
  call_time: string;
  event_start: string;
  event_end: string;
  pack_out: string;
  ready_by: string;
  notes: string;
}

/**
 * Section `job_details` — applied per present key; blank strings coerce to
 * NULL server-side. `day_schedules` replaces the booking's full schedule set
 * when present, so omit the key entirely (don't send `undefined`) for
 * single-day events.
 */
export interface JobDetailsPatch {
  call_time?: string | null;
  pack_out_time?: string | null;
  site_available_from?: string | null;
  call_out_notes?: string | null;
  vehicle_type?: string | null;
  vehicle_amount?: number | string | null;
  vehicle_contractor_id?: string | null;
  band_names?: string | null;
  crew_count?: number | string | null;
  day_schedules?: DaySchedulePayload[];
}

/** Section `assignment_notes` — one assignment's tasks_description. */
export interface AssignmentNotesPatch {
  assignmentId: string;
  tasks_description?: string | null;
}

/** Section `header_notes` — operator-only free text; no downstream consumer. */
export interface HeaderNotesPatch {
  notes?: string | null;
}

/**
 * Section `status_override` — display-only phase override. A null/blank phase
 * clears the override. The server stamps the current lifecycle phase as the
 * expiry base.
 */
export interface StatusOverridePatch {
  phase: BookingPhase | null;
}

export interface WorkspacePatchPayloads {
  event_info: EventInfoPatch;
  client_info: EventInfoPatch;
  quote_settings: QuoteSettingsPatch;
  job_details: JobDetailsPatch;
  assignment_notes: AssignmentNotesPatch;
  header_notes: HeaderNotesPatch;
  status_override: StatusOverridePatch;
}

export type WorkspaceSection = keyof WorkspacePatchPayloads;

/**
 * PATCH /api/admin/events/{id}/workspace — section-scoped field save.
 * Powers header autosave, quote-settings save, and job-details save.
 * The payload type is keyed off the section, so the compiler enforces the
 * server's field whitelist at every call site.
 */
export async function patchWorkspace<S extends WorkspaceSection>(
  id: string,
  section: S,
  data: WorkspacePatchPayloads[S],
): Promise<void> {
  await apiFetch<unknown>(`/api/admin/events/${id}/workspace`, {
    method: "PATCH",
    body: { section, data },
  });
}

/**
 * One row of a gear-list replace. Mirrors gearItemPayload in
 * go-service/internal/api/gear_items.go — unknown keys are ignored, so extra
 * UI-side fields (clientKey, suppliers, …) are harmless to send but only
 * these persist.
 */
export interface GearItemPayload {
  /**
   * Existing booking_items id. Read server-side for `logistics` rows only,
   * where it names the server-owned transport line this row's edits merge
   * into (#243 sweep 2 D4); every other type is delete+reinserted, so its id
   * is ignored.
   */
  id?: string;
  item_name: string;
  quantity?: number | string | null;
  days?: number | string | null;
  starts_on?: string | null;
  ends_on?: string | null;
  notes?: string | null;
  equipment_id?: string | null;
  hire_rate?: number | string | null;
  type?: string | null;
  manual_override?: boolean;
  substituted_from_equipment_id?: string | null;
  substitution_reason?: string | null;
  /** Quote heading this line renders under (#21); blank re-derives default. */
  quote_group?: string | null;
  /** Flat $ discount off the line total (#21). */
  discount?: number | null;
  /** Per-section summarise toggle — collapses the group on the client quote. */
  quote_group_summarised?: boolean;
  /**
   * Quote package this row belongs to (#20). Must reference a key in the
   * PUT body's packages list; null/absent only on a no-packages booking.
   */
  package_key?: string | null;
  /** Stable matching identity for planned labour; carried through saves. */
  labour_category?: "setup" | "operation" | "pack_down" | "travel" | null;
}

/** One package in a gear-list replace (#20). Keys are client-minted stable ids. */
export interface QuotePackagePayload {
  key: string;
  name: string;
  position: number;
}

/**
 * PUT /api/admin/events/{id}/gear-items — replace the gear list (and sync
 * the booking's quote packages).
 *
 * Optimistic concurrency (#242 sweep 2 C2): `itemsRevision` echoes
 * `booking.items_revision` from the /details read this editor state was
 * seeded from. When another writer (a second operator's tab, a transport
 * compute) has bumped it since, the server refuses the whole replace with
 * a 409 (`ApiError.status === 409`) instead of silently overwriting —
 * callers refetch on that. Returns the post-save revision so the caller
 * can advance its echo without waiting for a refetch; null when the
 * server predates the field.
 */
export async function putGearItems(
  id: string,
  gearItems: GearItemPayload[],
  packages: QuotePackagePayload[] = [],
  itemsRevision: number | null = null,
): Promise<number | null> {
  const normalizedGearItems = gearItems.map((item) => ({
    ...item,
    type: normalizeBookingItemType(item.type),
  }));
  const body: Record<string, unknown> = {
    gearItems: normalizedGearItems,
    packages,
  };
  if (itemsRevision != null) body.items_revision = itemsRevision;
  const res = await apiFetch<{ items_revision?: number }>(
    `/api/admin/events/${id}/gear-items`,
    { method: "PUT", body },
  );
  return res?.items_revision ?? null;
}

/**
 * POST /api/admin/events/{id}/generate-quote-from-gear — rebuilds the quote
 * summary rows and the preview draft PDFs from the gear list.
 *
 * Despite the name it calls no AI: it is pure aggregation + pricing (the AI
 * quote pipeline is the inquiry path). Synchronous — it returns only once
 * every draft has been rendered, uploaded, and its file id persisted.
 */
export async function generateQuoteFromGear(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/admin/events/${id}/generate-quote-from-gear`, {
    method: "POST",
  });
}

/**
 * POST /api/admin/events/{id}/generate-job-summary — build job-sheet snapshot.
 */
export async function generateJobSummary(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/admin/events/${id}/generate-job-summary`, {
    method: "POST",
  });
}

/**
 * POST /api/admin/events/{id}/regear — re-pick gear for new dates.
 * EXISTS in Go (#72).
 */
export async function regear(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/admin/events/${id}/regear`, { method: "POST" });
}

/**
 * POST /api/admin/events/{id}/shortfalls/dismiss — clear the
 * gear-shortfall banner once the operator has resolved it off-system.
 */
export async function dismissShortfalls(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/admin/events/${id}/shortfalls/dismiss`, {
    method: "POST",
  });
}

/**
 * POST /api/admin/events/{id}/cancel — cancel event + assignments + notify.
 */
export async function cancelEvent(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/admin/events/${id}/cancel`, { method: "POST" });
}

/**
 * POST /api/admin/confirm-client-payment — confirm deposit/balance received.
 * EXISTS in Go (#70).
 */
export async function confirmClientPayment(
  bookingId: string,
  type: "deposit" | "balance",
  date: string,
): Promise<void> {
  await apiFetch<unknown>("/api/admin/confirm-client-payment", {
    method: "POST",
    body: { bookingId, type, date },
  });
}

/**
 * POST /api/admin/confirm-contractor-payment — confirm a contractor paid.
 * EXISTS in Go (#70).
 */
export async function confirmContractorPayment(
  assignmentId: string,
  date: string,
  hours: number,
): Promise<void> {
  await apiFetch<unknown>("/api/admin/confirm-contractor-payment", {
    method: "POST",
    body: { assignmentId, date, hours },
  });
}

/** POST /api/admin/send-to-client — send the quote PDF + email. */
export async function sendToClient(
  bookingId: string,
  notes: string | null,
  depositPercent: number,
  quoteValidDays: number,
): Promise<void> {
  await apiFetch<unknown>("/api/admin/send-to-client", {
    method: "POST",
    body: { bookingId, notes, depositPercent, quoteValidDays },
  });
}

/** POST /api/admin/send-quote-reminder. */
export async function sendQuoteReminder(bookingId: string): Promise<void> {
  await apiFetch<unknown>("/api/admin/send-quote-reminder", {
    method: "POST",
    body: { bookingId },
  });
}

/** POST /api/admin/send-deposit-reminder. */
export async function sendDepositReminder(bookingId: string): Promise<void> {
  await apiFetch<unknown>("/api/admin/send-deposit-reminder", {
    method: "POST",
    body: { bookingId },
  });
}

/** POST /api/admin/send-balance-invoice. */
export async function sendBalanceInvoice(bookingId: string): Promise<void> {
  await apiFetch<unknown>("/api/admin/send-balance-invoice", {
    method: "POST",
    body: { bookingId },
  });
}

/** POST /api/admin/send-balance-reminder. */
export async function sendBalanceReminder(bookingId: string): Promise<void> {
  await apiFetch<unknown>("/api/admin/send-balance-reminder", {
    method: "POST",
    body: { bookingId },
  });
}

/** POST /api/admin/send-contractor-reminder. */
export async function sendContractorReminder(bookingId: string): Promise<void> {
  await apiFetch<unknown>("/api/admin/send-contractor-reminder", {
    method: "POST",
    body: { bookingId },
  });
}

/** POST /api/admin/notify-contractors — send job offers (or renotify). */
export async function notifyContractors(
  token: string,
  bookingId: string,
  renotify: boolean,
): Promise<void> {
  await apiFetch<unknown>("/api/admin/notify-contractors", {
    method: "POST",
    body: { token, bookingId, renotify },
  });
}

/**
 * One crew member in a select-contractors save. Mirrors
 * selectContractorsAssignment in go-service/internal/api/contractors.go.
 */
export interface CrewAssignmentPayload {
  contractor_id: string;
  hourly_rate: number;
  estimated_hours: number;
  pay_amount: number;
  tasks_description: string | null;
  jobsheet_template_id: string | null;
  /** The vehicle this Contractor drives — a Booking vehicle's name, or "" for none. */
  vehicle_name: string | null;
}

/**
 * POST /api/select-contractors — persist the crew composition.
 */
export async function selectContractors(
  token: string,
  bookingId: string,
  assignments: CrewAssignmentPayload[],
): Promise<void> {
  await apiFetch<unknown>("/api/select-contractors", {
    method: "POST",
    body: { token, bookingId, assignments },
  });
}

/**
 * DELETE /api/admin/select-contractors — remove a single assignment.
 *
 * Sits under /api/admin, unlike the token-addressed POST above: removing an
 * assignment is an operator action on their own booking, and this call has
 * never sent a token. It previously hit the unauthenticated /api/select-
 * contractors, where the booking/assignment id pair was the only thing
 * standing between any caller and cancelling another company's crew (#82).
 */
export async function removeAssignment(
  bookingId: string,
  assignmentId: string,
): Promise<void> {
  await apiFetch<unknown>("/api/admin/select-contractors", {
    method: "DELETE",
    body: { bookingId, assignmentId },
  });
}
